/**
 * Everything in a scene that can be exported, as one flat list.
 *
 * A screen, a whole flipbook and a single flipbook page differ only in WHICH
 * frames get rendered and HOW LONG each one holds — so each target carries
 * exactly that, and every export kind collapses into a single `recordScreens`
 * call (see planScreens below).
 */
import { getScreenDurationMs } from "@excalidraw/common";

import type {
  ExcalidrawElement,
  ExcalidrawFrameLikeElement,
} from "@excalidraw/element/types";

import {
  getFlipbookBooks,
  getFlipbookPages,
  getScreens,
} from "../../flipbook/ops";

import type { ScreenPlan } from "./record";

/** `<optgroup>` label; "" = ungrouped, listed first. */
export type ExportTargetGroup = "" | "Screens" | "Flipbooks" | "Pages";

export type ExportTarget = {
  /** stable `<select>` value — a frame id, or `book:<bookId>` */
  id: string;
  label: string;
  group: ExportTargetGroup;
  /** frames to render, in order */
  frames: readonly ExcalidrawFrameLikeElement[];
  /** default download basename, already filesystem-safe */
  fileBase: string;
  /** per-frame hold; undefined = each frame's own screenDurationMs */
  holdMs?: number;
};

export const ALL_SCREENS_ID = "all-screens";

export const bookTargetId = (bookId: string) => `book:${bookId}`;

/** strip characters Windows/macOS reject in filenames, collapse the rest */
export const toFileBase = (label: string): string =>
  label
    .replace(/[\\/:*?"<>|]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .toLowerCase() || "export";

const frameLabel = (
  frame: ExcalidrawFrameLikeElement,
  index: number,
  fallback: string,
) => frame.name?.trim() || `${fallback} ${index + 1}`;

/**
 * Every export target in the scene, in menu order: "All screens", then each
 * screen, each flipbook, and each flipbook page.
 *
 * `flipbookFps` is the flipbook's own playback rate — it sets how long each
 * page holds. The encoder fps is separate and only decides how finely that
 * hold is sampled, so a 12fps book exported at 30fps is still a 12fps book.
 */
export const buildExportTargets = (
  elements: readonly ExcalidrawElement[],
  flipbookFps: number,
): ExportTarget[] => {
  const targets: ExportTarget[] = [];

  const screens = getScreens(elements);
  if (screens.length > 1) {
    targets.push({
      id: ALL_SCREENS_ID,
      label: `All screens (${screens.length})`,
      group: "",
      frames: screens,
      fileBase: "screens",
    });
  }
  screens.forEach((frame, index) => {
    const label = frameLabel(frame, index, "Screen");
    targets.push({
      id: frame.id,
      label,
      group: "Screens",
      frames: [frame],
      fileBase: toFileBase(label),
    });
  });

  // guard the divisor here, not at the call site: a mid-edit fps field can be
  // 0 or NaN, and holdMs must never be Infinity (recordScreens would plan an
  // unbounded sample count and only fail at the frame cap).
  const holdMs = 1000 / Math.min(60, Math.max(1, Math.round(flipbookFps) || 1));

  const books = getFlipbookBooks(elements);
  const pagesByBook = new Map(
    books.map((book) => [book.id, getFlipbookPages(elements, book.id)]),
  );

  for (const book of books) {
    targets.push({
      id: bookTargetId(book.id),
      label: `${book.name} (${book.pageCount} ${
        book.pageCount === 1 ? "page" : "pages"
      })`,
      group: "Flipbooks",
      frames: pagesByBook.get(book.id) ?? [],
      fileBase: toFileBase(book.name),
      holdMs,
    });
  }

  for (const book of books) {
    (pagesByBook.get(book.id) ?? []).forEach((frame, index) => {
      const label = frameLabel(frame, index, "Page");
      targets.push({
        id: frame.id,
        label: `${book.name} · ${label}`,
        group: "Pages",
        frames: [frame],
        fileBase: toFileBase(`${book.name}-${label}`),
      });
    });
  }

  return targets;
};

/** The `screens` argument for recordScreens — the one shape all kinds share. */
export const planScreens = (target: ExportTarget): ScreenPlan[] =>
  target.frames.map((frame) => ({
    frame,
    durationMs: target.holdMs ?? getScreenDurationMs(frame),
  }));

/**
 * The target matching `id`, else the first one. Always resolved against the
 * live scene — a stored id goes stale the moment its frame is deleted (same
 * reasoning as flipbook's resolveBookId).
 */
export const resolveTarget = (
  targets: readonly ExportTarget[],
  id: string | null,
): ExportTarget | null =>
  targets.find((target) => target.id === id) ?? targets[0] ?? null;
